/**
 * useFileUpload Hook
 * Manages file selection and upload state
 * Extracted from DocumentDialog for reusability and testability
 */

import { useState, useRef, useCallback } from 'react';

// SECURITY: VULN-020 FIX - Client-side file size validation (UX improvement)
// Note: Backend validation (already implemented) is the actual security layer
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB (matches backend limit)
const MAX_FILE_SIZE_MB = 100;

export interface UseFileUploadReturn {
  selectedFile: File | null;
  uploadingFile: boolean;
  uploadProgress: number;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  fileError: string | null;
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleFileBrowse: () => void;
  handleFileRemove: () => void;
  setUploadingFile: React.Dispatch<React.SetStateAction<boolean>>;
  setUploadProgress: React.Dispatch<React.SetStateAction<number>>;
  resetFile: () => void;
  maxFileSizeMB: number;
}

/**
 * Custom hook for managing file upload state
 */
export const useFileUpload = (): UseFileUploadReturn => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileError, setFileError] = useState<string | null>(null);

  /**
   * Validate file size
   */
  const validateFileSize = useCallback((file: File): { valid: boolean; error?: string } => {
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: `File size exceeds ${MAX_FILE_SIZE_MB}MB limit. Selected file is ${(file.size / 1024 / 1024).toFixed(2)}MB.`
      };
    }
    return { valid: true };
  }, []);

  /**
   * Handle file selection from input with validation
   */
  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {return;}

    // Validate file size
    const sizeValidation = validateFileSize(file);
    if (!sizeValidation.valid) {
      setFileError(sizeValidation.error || 'File validation failed');
      setSelectedFile(null);
      // Clear the input to allow re-selecting the same file after fixing
      if (e.target) {
        e.target.value = '';
      }
      return;
    }

    // Clear any previous errors
    setFileError(null);
    setSelectedFile(file);
  }, [validateFileSize]);

  /**
   * Trigger file input click
   */
  const handleFileBrowse = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /**
   * Remove selected file
   */
  const handleFileRemove = useCallback(() => {
    setSelectedFile(null);
    setUploadProgress(0);
    setFileError(null);
    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  /**
   * Reset all file upload state
   */
  const resetFile = useCallback(() => {
    setSelectedFile(null);
    setUploadingFile(false);
    setUploadProgress(0);
    setFileError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  return {
    selectedFile,
    uploadingFile,
    uploadProgress,
    fileInputRef,
    fileError,
    handleFileSelect,
    handleFileBrowse,
    handleFileRemove,
    setUploadingFile,
    setUploadProgress,
    resetFile,
    maxFileSizeMB: MAX_FILE_SIZE_MB
  };
};
